// ---------------------------------------------------------------------------
// PUBLIC PAGE REGISTRY — SINGLE SOURCE OF TRUTH for every public marketing page.
// ===========================================================================
//
// A public page used to be "SEO-correct" only if it was remembered in FOUR
// separate hand-kept lists, which had already drifted apart from each other:
//
//   1. the prerender readiness beacon set  (web/src/prerender.ts PRERENDER_ROUTES)
//   2. the per-route head/meta map          (web/src/seo.ts ROUTE_SEO)
//   3. the breadcrumb JSON-LD labels        (web/src/structuredData.ts BREADCRUMB_LABELS)
//   4. the sitemap's static-path list       (methods/src/common/sitemap.ts SITEMAP_STATIC_PATHS)
//      — which the SEO Health Monitor also reads (methods/src/common/seoHealth.ts).
//
// This module collapses those four into ONE registration. Register a page here
// and, by construction, it is prerendered, in the sitemap, correctly titled and
// described, breadcrumbed, and watched by the health monitor — there is no
// longer a state where a page is in one list but missing from another.
//
// WHY IT LIVES HERE (methods/src/common, not the web app): this is the one place
// both the backend (sitemap, health monitor) and the frontend (prerender beacon,
// head/meta, structured data) can import from — the same cross-boundary pattern
// pricing.ts / searchPricing.ts / metrics.ts already use. It MUST stay
// dependency-free (no `@mindstudio-ai/agent`, no React, no DOM) or the browser
// build breaks; everything here is plain data and pure string helpers.
//
// ---------------------------------------------------------------------------
// THE ONE MANUAL MIRROR: web.json's `prerender.paths`.
// The platform's prerender opt-in lives in dist/interfaces/web/web.json, a static
// config file that cannot import code — so it stays a hand-maintained mirror of
// this registry: every PUBLIC_PAGES path, PLUS the blog routes `/blog` and
// `/blog/*` (the blog renders its own head via document.write and is NOT an
// SPA-managed page in this registry — see blog/render.mjs + DynamicBlog.tsx).
// The SEO Health Monitor is the backstop: if a page is ever registered here but
// missing from web.json, the monitor fetches it, sees the empty SPA shell, and
// flags it within a day. So the drift this registry exists to prevent (a page
// missing from the CODE lists) is now impossible, and the one remaining manual
// step (web.json) is caught by monitoring rather than by Search Console months
// later. When you add a page here, add its path to web.json too.
// ---------------------------------------------------------------------------

/** One registration for a public, prerendered, SPA-rendered marketing page. */
export interface PublicPage {
  /** Exact pathname, e.g. '/plans'. The canonical URL is `${origin}${path}`. */
  path: string;
  /** <title> + og:title + twitter:title. Kept <= ~60 chars for search snippets. */
  title: string;
  /** meta description + og/twitter description. Kept <= ~160 chars. */
  description: string;
  /**
   * Breadcrumb trail label (Home > <label>) for this sub-page's BreadcrumbList
   * JSON-LD. Omit on the home page (it IS the root of the trail).
   */
  breadcrumbLabel?: string;
  /**
   * Whether this page appears in sitemap.xml. Defaults to true. A page can be
   * prerendered + head-managed here yet deliberately kept out of the sitemap;
   * none are today, but the flag keeps that a one-word decision, not a fork.
   */
  includeInSitemap?: boolean;
  /**
   * Which route-specific structured-data nodes this page carries beyond the
   * global Organization + WebSite identity:
   *   'home' — the SoftwareApplication + FAQPage nodes (home only).
   *   omitted — a BreadcrumbList built from `breadcrumbLabel` (every sub-page).
   * Replaces the hardcoded `pathname === '/'` check that used to live in
   * structuredData.ts, so the home special-casing is a registry fact too.
   */
  jsonLd?: 'home';
  /**
   * Optional per-page Open Graph / Twitter share image (absolute URL or a
   * site-relative path). Omit to inherit the brand default set in index.html —
   * which every page does today. Present here for extensibility: a future page
   * with a bespoke share card sets it once and every surface honors it.
   */
  ogImage?: string;
  /**
   * Optional FAQ for a tool/landing page. When present, the page renders these
   * exact Q&A pairs AND the structured-data layer emits a matching FAQPage node
   * (see structuredData.ts) — one source, so the visible copy and the JSON-LD
   * can never drift (Google requires them to match). The home page keeps its own
   * FAQ (src/content/faq.ts); this is for the free-tool pages that carry their own.
   */
  faq?: { q: string; a: string }[];
  /**
   * When present, this page is a free, public, no-signup tool and is surfaced in
   * the site footer's "Free Tools" column. `footerLabel` is the short nav text —
   * the SEO `title` is written for search snippets and is too long for a footer
   * link, and it doubles as the internal-link anchor text (so keep it a clean,
   * keyword-true phrase like "Patent Cost Calculator"). Pages appear in registry
   * order. Register a new calculator with a `freeTool` label and it shows up in
   * the footer automatically — there is no separate hand-kept footer list to
   * drift out of sync, the same drift-prevention discipline this registry exists
   * for.
   */
  freeTool?: {
    footerLabel: string;
    /**
     * The short answer the tool gives, as one quotable paragraph with real figures (the passage AI answer
     * engines lift). Rendered under the page heading and used as the WebApplication description. Every
     * figure must already appear in this page's own FAQ or calculator, so the two never disagree.
     */
    quickAnswer?: string;
    /** schema.org applicationCategory for the tool's WebApplication node. */
    category?: 'FinanceApplication' | 'BusinessApplication' | 'UtilitiesApplication';
  };
  /**
   * Time-sensitive pages (fees, prices): a dated title and description that carry the current year, e.g.
   * "Patent Cost Calculator: 2026 USPTO Fee Breakdown". `{year}` is filled in with the current year, but
   * only while the page's figures were reviewed this year (`reviewedOn`, YYYY-MM-DD). Once the year turns
   * and nobody has re-checked them, the page falls back to the undated `title` / `description`, so it
   * never shows a stale year. Re-check the figures each January and bump `reviewedOn`.
   */
  dated?: { reviewedOn: string; title: string; description: string };
}

/** The dated title/description when the page was reviewed this year, otherwise the undated copy. */
export function withCurrentYear(page: PublicPage, now: Date = new Date()): PublicPage {
  if (!page.dated) return page;
  const year = now.getFullYear();
  if (Number(page.dated.reviewedOn.slice(0, 4)) !== year) return page;
  const fill = (s: string) => s.split('{year}').join(String(year));
  return { ...page, title: fill(page.dated.title), description: fill(page.dated.description) };
}

/**
 * Every public, indexable marketing page, in the order they're opted into
 * prerendering in web.json. Copy discipline (unchanged from the old ROUTE_SEO):
 * every line factual and verifiable, no hype, "verdict"/"prior art" kept as
 * owned terms, titles <= ~60 chars, descriptions <= ~160 chars.
 *
 * NOT here: /blog and /blog/* (blog renders its own head — see the mirror note
 * above), and the noindex routes /try, /signup, /strategy and the A/B variants
 * (those are asserted noindex in seo.ts — a separate concern from public pages).
 */
const REGISTERED_PAGES: PublicPage[] = [
  {
    path: '/',
    title: 'EntreDash: From a Random Idea to a Real Business',
    description:
      'EntreDash is your AI advisory board for taking an idea from spark to business. Test whether your idea holds up, free: a guided strategy session and a verdict backed by real prior-art research, not guesses.',
    jsonLd: 'home', // home carries the SoftwareApplication + FAQPage nodes; no breadcrumb (it's the root)
  },
  {
    path: '/plans',
    title: 'EntreDash Pricing: Free Plan to Pro',
    description:
      'Start free with one strategy session a month. Add credit packs that never expire, or subscribe to Standard, Plus, or Pro and save with annual billing.',
    breadcrumbLabel: 'Pricing',
  },
  {
    path: '/methodology',
    title: 'How EntreDash Reaches a Verdict',
    description:
      'See the sources behind every verdict: the databases EntreDash searches for prior art and the citations backing each report.',
    breadcrumbLabel: 'How We Reach a Verdict',
  },
  {
    path: '/how-it-works',
    title: 'How EntreDash Works: Idea to Invention',
    description:
      'From a rough idea to a buildable, patentable invention: strategy sessions, prior-art research, a design workspace, and business planning tools.',
    breadcrumbLabel: 'How It Works',
  },
  {
    path: '/spark',
    title: 'Spark: Turn a Frustration Into a Business Idea',
    description:
      'No idea yet? Spark turns an everyday frustration into concrete, buildable business directions in minutes. Free to try, no formed idea required.',
    // Audit fix: /spark was prerendered + had ROUTE_SEO but carried NO breadcrumb
    // node. Registering it once closes that latent gap.
    breadcrumbLabel: 'Spark',
    // Free, no-signup idea generator — surfaced in the footer's Free Tools column.
    freeTool: {
      footerLabel: "Idea Generator",
      category: 'BusinessApplication',
      quickAnswer:
        "No formed idea needed. Describe an everyday frustration and Spark turns it into concrete, buildable business directions in minutes, each grounded in the problem you brought rather than a random idea. It is free to try.",
    },
  },
  {
    path: '/students',
    title: 'EntreDash for Students and Families',
    description:
      'A guided path for student inventors and their families: run a strategy session, research prior art, and build a patentable idea. No law degree required.',
    breadcrumbLabel: 'For Students',
  },
  {
    path: '/learn',
    title: 'EntreDash Education Center for Inventors',
    description:
      'Free, structured lessons across three curriculums: intellectual property, product development, and commercialization. Read at your own pace, quiz when you want.',
    // Audit fix: /learn was prerendered + had ROUTE_SEO but carried NO breadcrumb.
    breadcrumbLabel: 'Education Center',
  },
  {
    path: '/marketplace',
    title: 'EntreDash Patent Attorney & Agent Marketplace',
    description:
      'Browse USPTO-registered patent attorneys and agents by state, specialty, and entity size, and find the right practitioner to take your invention forward.',
    // Audit fix: /marketplace was prerendered + had ROUTE_SEO but carried NO breadcrumb.
    breadcrumbLabel: 'Marketplace',
  },
  {
    path: '/affiliates',
    title: 'EntreDash Affiliate Program: Earn 40% Commission',
    description:
      'Earn 40% commission promoting EntreDash. Get paid on the first-year value of every annual plan, credit pack, and patent search your referrals buy. Free to join.',
    breadcrumbLabel: 'Affiliate Program',
  },
  {
    path: '/infographic',
    title: 'Spark to Business: the EntreDash One-Pager',
    description:
      'The whole spark-to-business journey on one page: how an everyday idea becomes a validated, researched, buildable invention. Download it here.',
    breadcrumbLabel: 'Spark to Business',
  },
  {
    path: '/about',
    title: 'About EntreDash',
    description:
      'EntreDash takes everyday people from a random idea to a buildable, patentable invention, using the same tools a law firm charges a fortune for.',
    breadcrumbLabel: 'About',
  },
  {
    path: '/contact',
    title: 'Contact EntreDash',
    description:
      'Questions about your account, a partnership, or anything else. Reach the EntreDash team directly.',
    breadcrumbLabel: 'Contact',
  },
  {
    path: '/terms',
    title: 'EntreDash Terms of Service',
    description: 'The terms that govern your use of EntreDash.',
    breadcrumbLabel: 'Terms of Service',
  },
  {
    path: '/privacy',
    title: 'EntreDash Privacy Policy',
    description: 'How EntreDash handles your data and protects the confidentiality of your ideas.',
    breadcrumbLabel: 'Privacy Policy',
  },
  {
    path: '/security',
    title: 'EntreDash Security',
    description: 'How EntreDash keeps your ideas confidential and your account secure.',
    breadcrumbLabel: 'Security',
  },
  {
    path: '/cookies',
    title: 'EntreDash Cookie Policy',
    description: 'How EntreDash uses cookies and similar technologies.',
    breadcrumbLabel: 'Cookie Policy',
  },

  // -------------------------------------------------------------------------
  // Free tools — public, no-signup SEO utilities. Zero-AI calculators built on
  // real, cited data. Each ranks for a high-intent query and hands off into the
  // product. Add each path to web.json's prerender.paths too.
  // -------------------------------------------------------------------------
  {
    path: '/patent-cost-calculator',
    title: 'Patent Cost Calculator: USPTO Fee Breakdown',
    description:
      'Estimate what a patent costs: current USPTO fees by entity size for provisional and utility applications, plus a typical attorney range. Free, no sign-up.',
    // Fees checked against the live USPTO schedule (effective Jan 19, 2025, unchanged through 2026).
    dated: {
      reviewedOn: '2026-10-04',
      title: 'Patent Cost Calculator: {year} USPTO Fee Breakdown',
      description: 'Estimate what a patent costs in {year}: current USPTO fees by entity size for provisional and utility applications, plus a typical attorney range. Free.',
    },
    breadcrumbLabel: 'Patent Cost Calculator',
    // Free, no-signup calculator — surfaced in the footer's Free Tools column.
    freeTool: {
      footerLabel: "Patent Cost Calculator",
      category: 'FinanceApplication',
      quickAnswer:
        "A US provisional patent application costs $65 (micro entity), $130 (small entity) or $325 (large entity) in USPTO filing fees, effective January 19, 2025. Preparing it well typically adds $1,500 to $5,500 in attorney or agent fees. A provisional holds your filing date for 12 months and is never examined; the non-provisional that follows carries its own filing, search and examination fees.",
    },
    faq: [
      {
        q: 'How much does a provisional patent cost?',
        a: "The USPTO filing fee for a provisional patent application is $65 for a micro entity, $130 for a small entity, and $325 for a large entity (effective January 19, 2025). Preparing it well typically adds $1,500 to $5,500 in attorney or agent fees, depending on complexity. A provisional holds your filing date for 12 months but is never examined and never becomes a patent on its own.",
      },
      {
        q: 'What is the difference between a provisional and a non-provisional patent application?',
        a: 'A provisional application is a lower-cost placeholder that secures your filing date for 12 months and is never examined. A non-provisional utility application is the one the USPTO actually examines and that can mature into a granted patent; it carries separate filing, search, and examination fees. Most inventors file a provisional first, then a non-provisional within the 12-month window.',
      },
      {
        q: 'What is a micro entity, and do I qualify?',
        a: "Micro entity is the USPTO's smallest fee tier, giving an 80% discount on most fees. It is generally for independent inventors under a gross-income cap who have filed few prior applications. Small entity, a 60% discount, is the broader tier for businesses under 500 employees. The exact income limit changes yearly, so confirm your status against the USPTO's current rule before you file.",
      },
      {
        q: 'Do I need a patent attorney, or can I file myself?',
        a: 'You can legally file yourself (pro se), and the USPTO fees are the same either way. But claim drafting is where most of the patentable value is won or lost, and a poorly written application can leave you with far less protection than you think. Most inventors of anything commercially important use a registered patent attorney or agent, which is why attorney fees usually dwarf the USPTO fees.',
      },
      {
        q: 'How much are patent maintenance fees?',
        a: 'After a utility patent grants, the USPTO charges maintenance fees at 3.5, 7.5, and 11.5 years to keep it in force. At the small-entity rate these are currently $860, $1,616, and $3,312 (large entity: $2,150 / $4,040 / $8,280; micro entity: $430 / $808 / $1,656). Miss one and the patent expires. This calculator covers filing costs, not the maintenance timeline.',
      },
      {
        q: 'Is this estimate legal advice?',
        a: 'No. This is an educational estimate built from the published USPTO fee schedule and typical attorney-fee surveys, not a quote and not legal advice. Whether a patent is worth the cost depends on whether your idea is defensible and the market is real, which is exactly what a free EntreDash strategy session helps you figure out first.',
      },
    ],
  },
  {
    path: '/provisional-patent-deadline-calculator',
    title: 'Provisional Patent Deadline Calculator (12-Month)',
    description:
      'Enter your US provisional filing date and get the exact 12-month non-provisional deadline, the parallel foreign/PCT deadline, and how much time is left. Free, no sign-up.',
    breadcrumbLabel: 'Provisional Deadline Calculator',
    // Free, no-signup calculator — surfaced in the footer's Free Tools column.
    freeTool: {
      footerLabel: "Provisional Deadline Calculator",
      category: 'UtilitiesApplication',
      quickAnswer:
        "You have exactly 12 months from your US provisional filing date to file the non-provisional (35 U.S.C. § 119(e)). It is the 12-month anniversary, not a 365-day count, and it moves to the next business day if it lands on a weekend or a federal holiday. The foreign and PCT deadline runs on the same 12 months.",
    },
    faq: [
      {
        q: 'How long do I have to file a non-provisional after a provisional?',
        a: "Exactly 12 months from your provisional's filing date. Under 35 U.S.C. § 119(e), a non-provisional (utility) application must be filed within that year to claim the provisional's earlier filing date. If the 12-month date falls on a weekend or a federal holiday, it rolls to the next business day. Miss the window and, absent a narrow restoration petition, you lose the provisional's priority date.",
      },
      {
        q: 'Is the provisional deadline exactly 12 months?',
        a: 'Yes. It is the 12-month anniversary of the filing date, not a rolling 365-day count, so a provisional filed on March 15 is due the following March 15. The only adjustment is the business-day roll: if that anniversary is a Saturday, Sunday, or a Washington, DC federal holiday, the deadline moves to the next business day (37 CFR 1.7).',
      },
      {
        q: 'Can the 12-month provisional deadline be extended?',
        a: 'Not in the ordinary way. The 12-month benefit period is statutory and cannot be extended with the routine "extension of time" fees that apply to many other USPTO deadlines (37 CFR 1.78(k)). The only relief is a narrow petition to restore the benefit, filed within about two months of the deadline, showing the delay was unintentional and paying a fee. It can be refused.',
      },
      {
        q: 'What happens if I miss my provisional patent deadline?',
        a: 'The provisional is abandoned at 12 months and cannot be revived, and a non-provisional filed after the window generally cannot claim its priority date. One narrow path remains: within about two months you may petition under 37 CFR 1.78(b) to restore the benefit if the delay was unintentional, with a fee. Because it can be refused, it is a last resort, not a plan.',
      },
      {
        q: 'Is the foreign filing deadline the same as the US deadline?',
        a: "It falls on the same calendar day but is a separate legal right. Filing the provisional also starts a 12-month Paris Convention clock for filing abroad, or a PCT application, that claims the provisional's date (Paris Convention Article 4). Missing the US deadline does not waive the foreign one, or vice versa. Restoring a late foreign or PCT claim is governed by each country's own rules and is not guaranteed the way US restoration is.",
      },
      {
        q: 'What if my provisional deadline falls on a weekend or holiday?',
        a: 'It rolls forward. Under 37 CFR 1.7, when the 12-month date lands on a Saturday, Sunday, or a federal holiday in Washington, DC, the deadline moves to the next business day. This calculator applies that roll automatically and shows you the adjusted date, so your real deadline can be a day or two later than the plain anniversary.',
      },
    ],
  },
  {
    path: '/patent-maintenance-fee-calculator',
    title: 'Patent Maintenance Fee Calculator & Due Dates',
    description:
      'Enter your US patent grant date and see every maintenance fee — the 3.5, 7.5, and 11.5-year amounts by entity size, exact due dates, and the next one due. Free, no sign-up.',
    dated: {
      reviewedOn: '2026-10-04',
      title: 'Patent Maintenance Fee Calculator {year} & Due Dates',
      description: 'Enter your US patent grant date and see {year} maintenance fees: the 3.5, 7.5 and 11.5-year amounts by entity size, exact due dates, and the next one due. Free.',
    },
    breadcrumbLabel: 'Maintenance Fee Calculator',
    // Free, no-signup calculator — surfaced in the footer's Free Tools column.
    freeTool: {
      footerLabel: "Maintenance Fee Calculator",
      category: 'FinanceApplication',
      quickAnswer:
        "US utility patents carry maintenance fees at 3.5, 7.5 and 11.5 years after grant. For a small entity the current USPTO fees are $860, $1,616 and $3,312 (effective January 19, 2025); micro entities pay half and large entities about 2.5 times as much. Each fee can be paid from six months before its due date, with a six-month grace period after it at a surcharge.",
    },
    faq: [
      {
        q: 'When is the first patent maintenance fee due?',
        a: 'The first maintenance fee is due 3.5 years after your patent\u2019s grant date. You can pay as early as the 3-year mark with no surcharge. Miss 3.5 years, and a six-month grace period runs to the 4-year mark, during which you can still pay with a surcharge added.',
      },
      {
        q: 'How much are patent maintenance fees?',
        a: 'For a small entity, the current USPTO fees are $860 at 3.5 years, $1,616 at 7.5 years, and $3,312 at 11.5 years (effective January 19, 2025). Micro entities pay half those amounts; large entities pay about 2.5 times as much. These are government fees only, and the USPTO adjusts them periodically.',
      },
      {
        q: 'What happens if I miss a maintenance fee deadline?',
        a: 'You get a six-month grace period after each due date, during which you can still pay the fee plus a surcharge. If the grace period ends without payment, the patent expires. After that, the only option is a petition to reinstate it under 37 CFR 1.378, showing the delay was unintentional, which carries a fee and is not guaranteed.',
      },
      {
        q: 'Do design and plant patents have maintenance fees?',
        a: 'No. Maintenance fees apply only to utility patents. Design and plant patents owe nothing at any point: once granted, they stay in force for their full term with no further payments to the USPTO. A design patent runs 15 years from grant, a plant patent 20 years from filing.',
      },
      {
        q: 'Can I pay all three maintenance fees at once, or pay early?',
        a: 'No. Each fee can only be paid inside its own window, which opens six months before the due date. You cannot prepay years in advance, so you have to track and pay each fee on its own schedule as it comes due.',
      },
      {
        q: 'Do small or micro entity discounts really change the cost?',
        a: 'Substantially. A small entity pays 60% less than the standard rate and a micro entity 80% less, and the discount applies to every maintenance fee. Over the life of a patent that adds up to thousands of dollars, so it is worth confirming you qualify before each payment.',
      },
    ],
  },

  {
    path: '/patentability-checker',
    title: 'Is My Idea Patentable? Free Patentability Checker',
    description:
      'Describe your idea and get a free first reading against the four patent tests: subject matter, novelty, non-obviousness and usefulness. No sign-up.',
    breadcrumbLabel: 'Patentability Checker',
    // Free, no-signup AI tool (first of the AI converter wave). Footer label is
    // the query itself, which is also the internal-link anchor text.
    freeTool: {
      footerLabel: "Is My Idea Patentable?",
      category: 'BusinessApplication',
      quickAnswer:
        "No tool can say for certain whether an idea is patentable; only an examiner reviewing a filed application can. This free checker gives a first reading of your description against the four patent tests (patent-eligible subject matter, novelty, non-obviousness and usefulness) and names the open questions. It is not a legal opinion and runs no prior-art search.",
    },
    faq: [
      {
        q: 'Can an AI tell me if my idea is patentable?',
        a: 'Not for certain. Nobody can, human or AI, until an examiner reviews a filed application. This checker gives you a first reading: how your description holds up against the four tests, and where the open questions are. It\u2019s not a legal opinion, and it runs no search. For an opinion, ask a patent attorney. To see what already exists, run a search.',
      },
      {
        q: 'Is my idea kept private?',
        a: 'Your description is never used to train AI, never published and never seen by anyone. It is sent to the AI model that writes your reading, and EntreDash does not save it. The only thing we keep is a count of how many readings your browser has used today, so the tool can stay free.',
      },
      {
        q: 'What if I already posted my idea online?',
        a: 'In the US, you have one year from your own first public disclosure to file. That includes a post, a sale, a crowdfunding page or a pitch. Most other countries have no grace period, so posting before you file can rule out protection there. If it\u2019s been more than a year, ask a patent attorney what\u2019s still open.',
      },
      {
        q: 'Do I need a prototype to patent an idea?',
        a: 'No. US patent law doesn\u2019t require a working prototype to get a patent. Your patent application has to describe the invention in enough detail that someone skilled in the field could make and use it. A prototype can still help, since it shows you whether the idea works and what is actually new about it.',
      },
      {
        q: 'How is this different from a prior art search?',
        a: 'This checker doesn\u2019t search anything. It reads your description against the four tests and names the area of patents your idea sits near. A prior art search looks for the actual patents and publications closest to your idea. EntreDash\u2019s Prior Art Search Engine does that, and a strategy session helps you decide what to search for.',
      },
      {
        q: 'What does the classification code mean?',
        a: 'Patent offices sort patents using the Cooperative Patent Classification (CPC). The four-character code, like A47G, names a broad area of that system, and it\u2019s where a search for your idea would likely start. It\u2019s an estimate of the neighborhood your idea sits in, not a search result, so treat it as a place to begin looking.',
      },
    ],
  },

  {
    path: '/how-to-develop-an-invention',
    title: 'How to Develop an Invention Idea: Early Idea to Attorney-Ready',
    description:
      'The seven steps to develop an invention idea, in the order that saves money: capture it, check the need, search prior art, draw and model it, price it, protect it, and get attorney-ready.',
    breadcrumbLabel: 'How to Develop an Invention',
    faq: [
      {
        q: 'What are the steps to develop an invention idea?',
        a: 'Capture the idea in a sentence or two, check that people need it, search for prior art, draw and model it, price the whole journey and choose your path, protect it while you work, and prepare a packet for a patent attorney. Doing the search before paying for design work is the step that saves the most money.',
      },
      {
        q: 'What should I do first with an invention idea?',
        a: 'Write it down: what it does, who it helps and how they handle the problem today. Then check whether people actually want it and whether something similar already exists, before you spend on drawings, prototypes or a patent.',
      },
      {
        q: 'What does attorney-ready mean?',
        a: 'It means a patent attorney or agent can start on your invention without spending billable hours working out what it is. Bring a plain description, evidence of need, a prior-art search report, a numbered sketch and 3D model, a dated record of your work, and your questions and budget.',
      },
      {
        q: 'How much does it cost to develop an invention idea?',
        a: 'It depends on the path. Hiring professionals and freelancers to take one idea to attorney-ready costs about $2,450 to $11,950, and much more if earlier ideas fail first. Doing it yourself costs less in money and far more in hours. Use the free invention development cost calculator to estimate your own numbers. Patent filing fees are separate.',
      },
      {
        q: 'Do I need a patent before I develop my invention?',
        a: 'No. Most inventors check the market, search prior art and make concept drawings before filing anything. Keep the idea confidential while you work, and in the US file within one year of your own public disclosure. Many other countries have no grace period, so ask an attorney before showing the idea publicly.',
      },
    ],
  },

  {
    path: '/cost-to-develop-an-invention',
    title: 'Upfront Cost to Develop an Invention: Idea to Filing-Ready',
    description:
      'The upfront cost to take one invention from early idea to a filing-ready provisional application, four ways, with sourced prices and the hidden cost of redoing work in the wrong order.',
    breadcrumbLabel: 'Cost to Develop an Invention',
    // Outside prices checked 2026-10-04 (pages/cost/ctdFigures.ts CHECKED_ON). Re-check each January.
    dated: {
      reviewedOn: '2026-10-04',
      title: 'Upfront Cost to Develop an Invention ({year}): Idea to Filing-Ready',
      description: 'The upfront cost in {year} to take one invention from early idea to a filing-ready application, four ways, with sourced prices and the hidden cost of redoing work.',
    },
    faq: [
      {
        q: 'How much does it cost to develop an invention idea?',
        a: 'Taking one idea from a rough concept to a provisional application ready to file typically costs about $3,950 to $21,950 when you hire professionals and freelancers in the right order: a consult, a patentability search and opinion, market validation, a business plan, concept sketches, a 3D CAD model and an attorney-drafted provisional. On EntreDash the same work costs $1,519.96 for one idea with the Provisional Patent Prep Plan, which covers the full prior-art search, the Pathway Navigator filing plan, Prepare My Filing and the Cost & Filing Manager. Many inventors add a patent attorney\u2019s review of the draft, $250 to $2,000. Patent office filing fees are extra on every path.',
      },
      {
        q: 'What does EntreDash cost end to end for one idea, up to a filing-ready application?',
        a: 'Everything EntreDash sells for one idea, from the first strategy sessions to a filing-ready draft, costs $1,669.93 bought one by one: memos $14.99, build tools $14.99, a Design Set $39.99, the prior-art search $499.99, the Pathway Navigator filing plan $499.99, Prepare My Filing $499.99 and the Cost & Filing Manager $99.99. With the Provisional Patent Prep Plan ($1,449.99, covering the search, Pathway Navigator, Prepare My Filing and the manager) the total is $1,519.96. With heavy use of every add-on (a larger 3D model, five rebuilds, an extra sketch, packs of part close-ups, renders and monitoring scans, and two extra filing-plan re-runs) it rises to $1,959.81 bought one by one, or $1,809.84 with the Prep Plan. So one idea end to end costs about $1,520 to $1,960 on EntreDash, or about $1,770 to $3,960 with a patent attorney reviewing your draft before you file ($250 to $2,000). Patent office fees are extra.',
      },
      {
        q: 'Why does the order of the work matter so much?',
        a: 'If you pay for sketches, CAD or drafting before a patent search, and the search finds something close, that work has to be redone or thrown away. In our worked example of five tries, the ideas that don\u2019t pan out cost $7,000 to $25,600 before the one that works. Searching first is the standard advice from patent practitioners.',
      },
      {
        q: 'Does EntreDash replace a patent attorney?',
        a: 'No. EntreDash gives you market, patent and financial memos, a prior-art search report, a 3D concept model, a filing plan and a guided draft of your provisional application, but not a legal opinion, legal privilege or someone to file for you. It helps you decide which idea is worth paying an attorney for, and gets you to that meeting with a draft to review rather than one to write from scratch.',
      },
      {
        q: 'Is an NDA enough to protect my idea with a freelancer?',
        a: 'Only if they sign one, and many freelancers won\u2019t. An NDA is a contract promise to keep your information confidential. It isn\u2019t a patent, it doesn\u2019t stop someone else developing a similar idea on their own, and you would have to sue to enforce it. Ownership of the files a freelancer makes needs its own written agreement. On EntreDash, a mutually binding confidentiality agreement is part of the Terms of Use, so it applies from the moment you sign up.',
      },
    ],
  },

  {
    path: '/invention-help',
    title: 'Who Can Help With an Invention Idea, and When',
    description:
      'Who can help with an invention idea: when to see a patent attorney, an engineer and an incubator, what to bring each of them, and how to rule weak ideas out cheaply first.',
    breadcrumbLabel: 'Who Can Help With an Invention Idea',
    // Outside prices checked 2026-10-16 (pages/cost/ctdFigures.ts sources 31-36). Re-check each January.
    dated: {
      reviewedOn: '2026-10-16',
      title: 'Who Can Help With an Invention Idea ({year}), and When',
      description: 'Who can help with an invention idea in {year}: when to see a patent attorney, an engineer and an incubator, and what to bring each of them.',
    },
    faq: [
      {
        q: 'Who can help me with an invention idea?',
        a: 'It depends on the stage. Test the idea yourself first: whether people want it, whether something similar exists, and whether the numbers work. Once an idea survives, a patent attorney or agent advises on protection and filing, a product designer or engineer turns it into a prototype, and an incubator or investor can back the business. EntreDash does the early testing and prepares what each of them needs.',
      },
      {
        q: 'When should I hire a patent attorney for my invention?',
        a: 'Once you have an idea that has survived a market check and a prior-art search, and before you disclose it publicly. In the US you have one year from your own public disclosure to file. Most other countries have no grace period. A prepared package means fewer billable hours spent learning what the invention is.',
      },
      {
        q: 'Do I need a patent attorney to file a patent?',
        a: 'You can file yourself, but a registered patent attorney or agent improves the odds that the application protects what matters. A common middle path is to prepare your own draft and pay an attorney to review it. That typically costs $250 to $2,000, against $1,500 to $10,000 in attorney fees to draft a provisional from scratch.',
      },
      {
        q: 'Do I still need a patent attorney or engineer if I use EntreDash?',
        a: "Yes, once an idea is worth it. EntreDash tests ideas, searches prior art, models the design and helps you draft, so that by the time you pay a professional, you're paying for their judgment, not for them to catch up. It does not give legal opinions or file patents.",
      },
      {
        q: 'How much does it cost to screen invention ideas before paying a professional?',
        a: 'On EntreDash, screening 100 ideas and variations down to 3 finalists costs about $225, in an illustrative funnel. A first market read on one idea costs about $5.',
      },
    ],
  },

  {
    path: '/prepare-for-patent-attorney',
    title: 'How to Prepare for a Patent Attorney Consultation',
    description:
      'How to prepare for a patent attorney consultation: what to bring, what it costs, the questions to ask, and how to turn your first meeting into strategy instead of catch-up.',
    breadcrumbLabel: 'Prepare for a Patent Attorney',
    dated: {
      reviewedOn: '2026-10-16',
      title: 'How to Prepare for a Patent Attorney Consultation ({year})',
      description: 'How to prepare for a patent attorney consultation in {year}: what to bring, what it costs, and the questions to ask.',
    },
    faq: [
      {
        q: 'How do I prepare for a patent attorney consultation?',
        a: 'Bring a plain description of the invention and how it works, evidence that people want it, a prior-art search report, numbered drawings or a 3D model, a dated record of your work and who has seen it, and your markets, timeline and budget. The more of this you bring, the more of the meeting goes on strategy rather than catch-up.',
      },
      {
        q: 'What should I bring to my first meeting with a patent attorney?',
        a: 'A description and drawings of the invention, what you found when you searched for similar patents and products, a list of everyone you have shown it to and when, and your questions about what to protect, where to file and what it will cost.',
      },
      {
        q: 'How much does a patent attorney consultation cost?',
        a: 'Many patent attorneys offer a free first consultation. Paid consultations are often $200 to $500, and hourly rates are commonly $400 to $600.',
      },
      {
        q: 'What questions should I ask a patent attorney?',
        a: 'Ask what can be protected and how, whether to file a provisional first, where to file, what it will cost through filing and whether flat fees are available, whether they will review a draft you prepare, and how the closest prior art affects what you can claim.',
      },
      {
        q: 'Can a patent attorney review an application I drafted myself?',
        a: "Yes. Many attorneys and agents will review and file an inventor's own draft, typically for $250 to $2,000, against $1,500 to $10,000 in attorney fees to draft a provisional from scratch.",
      },
    ],
  },

  {
    path: '/invention-development-cost-calculator',
    title: 'Invention Development Cost Calculator: Idea to Filing-Ready',
    description:
      'Estimate the upfront cost and time to take an invention from early idea to a filing-ready provisional application: EntreDash, hiring it out, or DIY with software. Free, no sign-up.',
    // Same figures as /cost-to-develop-an-invention (pages/cost/ctdFigures.ts CHECKED_ON). Re-check each January.
    dated: {
      reviewedOn: '2026-10-04',
      title: 'Invention Development Cost Calculator ({year})',
      description: 'Estimate the upfront cost and time in {year} to take an invention from early idea to a filing-ready application, three ways. Free, no sign-up.',
    },
    breadcrumbLabel: 'Invention Development Cost Calculator',
    freeTool: {
      footerLabel: "Invention Development Cost Calculator",
      category: 'FinanceApplication',
      quickAnswer:
        "Taking an invention from early idea to a provisional application ready to file costs about $12,450 to $60,850 when you hire professionals and freelancers, using the calculator's defaults: five ideas screened, the best one refined with five redraws and five remodels, and an attorney drafting the provisional. The calculator sets that against doing it yourself with software and against EntreDash, side by side.",
    },
    faq: [
      {
        q: 'How much does it cost to develop an invention idea?',
        a: 'With the calculator\u2019s defaults (five ideas screened, the best one refined with five redraws and five remodels), taken through to a provisional application ready to file, hiring professionals and freelancers (including an attorney drafting the provisional) costs about $12,450 to $60,850, doing it yourself with software subscriptions costs about $979 to $2,239 plus 95 to 300 or more hours of your time, and EntreDash costs $1,660.86 with the Provisional Patent Prep Plan. Change the inputs to match your invention. Patent office filing fees are not included.',
      },
      {
        q: 'Why does screening more ideas cost so much when you hire it out?',
        a: 'Most first ideas don\u2019t survive a prior-art search or a look at the market, and the search is how you find out. Hiring it out, each idea that doesn\u2019t pan out still costs a patentability search and opinion, a consult and a CAD revision. On EntreDash, each idea screened is one set of three memos.',
      },
      {
        q: 'Is doing it yourself the cheapest way?',
        a: 'In dollars, DIY with software subscriptions is cheaper than hiring it out. Most of its cost is your time: learning CAD, learning to search patents, searching each idea carefully and writing the provisional yourself, which we estimate at 95 to 300 or more hours for a typical project. These hour figures are our estimates; there is no published study.',
      },
      {
        q: 'Does the estimate include an attorney?',
        a: 'Hiring it out already includes an attorney consult, a patentability search and opinion, and an attorney drafting the provisional. On EntreDash and DIY you write the provisional yourself; many inventors pay an attorney $250 to $2,000 to review the draft, which is not included. You can add an outside attorney search and opinion ($1,500 to $3,400) to those two paths with the toggle. Patent office filing fees are not included; use the patent cost calculator for those.',
      },
      {
        q: 'Is this a quote or legal advice?',
        a: 'No. It is an educational estimate from published prices, with our own assumptions labelled on the cost guide. EntreDash prices are pay-as-you-go with no plan. Prices for outside services vary with complexity and provider.',
      },
    ],
  },

  {
    path: '/hardware-development-cost-calculator',
    title: 'Hardware Development Cost Calculator: Cash to First Sale',
    description:
      'Estimate the cash a physical product needs before its first sale: prototype, moulds, safety testing by market, first factory order. Free, sourced, no sign-up.',
    // Figures in pages/cost/hardwareCostFigures.ts (CHECKED_ON). Re-check each quarter: lab prices and Amazon rules move.
    dated: {
      reviewedOn: '2026-10-15',
      title: 'Hardware Development Cost Calculator ({year}): Cash to First Sale',
      description: 'Estimate the cash a physical product needs in {year} before its first sale: prototype, moulds, safety testing by market, first factory order. Free.',
    },
    breadcrumbLabel: 'Hardware Development Cost Calculator',
    freeTool: {
      footerLabel: "Hardware Development Cost Calculator",
      category: 'FinanceApplication',
      quickAnswer:
        "A physical product typically needs about $35,500 to $160,500 in cash before its first sale, using the calculator's defaults: a Bluetooth product with a built-in lithium battery, four moulded plastic parts, and a first order of 1,000 units at $12 each, sold on Amazon in the US and Canada. That covers the prototype, injection moulds, safety and certification testing and the first factory order.",
    },
    faq: [
      {
        q: 'How much does it cost to develop a hardware product?',
        a: 'You need about $35,500 to $160,500 before your first sale, using the calculator\u2019s defaults: a Bluetooth product with a built-in lithium battery, 4 moulded plastic parts, and a first order of 1,000 units at $12 each, sold on Amazon in the US and Canada. That covers the prototype, injection moulds, safety and certification testing, and the first production run, including freight, boxes, a barcode and insurance. The range is wide mostly because of prototype studio fees and whether your battery cells already carry safety certificates.',
      },
      {
        q: 'Do I still need FCC testing if my Bluetooth or Wi-Fi module already has radio certification?',
        a: 'Yes, but less of it. The module\u2019s certification covers the radio itself, but the finished product still needs testing, which accredited lab Compliance Testing LLC prices at about $6,500 to $10,000. A custom radio design needs full radio certification: $9,000 to $12,000 in lab and certification fees, or $15,000 to $40,000 from consultancies once engineering and retests are included.',
      },
      {
        q: 'Is UL certification required to sell a plug-in product?',
        a: 'Not by US federal law, but major retailers require a safety listing from a recognised lab such as UL, ETL or CSA. Expect about $15,000 to $30,000, plus $12,000 to $20,000 a year in factory inspections. ETL often costs 25 to 50% less than UL.',
      },
      {
        q: 'Do I need UKCA marking if my product already has CE marking?',
        a: 'For most electronics, no. Great Britain accepts the CE mark, so EU testing also covers the UK. If you sell in the UK without the EU, UKCA testing costs about \u00a33,000 to \u00a38,000 without a radio and \u00a35,000 to \u00a315,000 with one.',
      },
      {
        q: 'How much does an injection mould cost?',
        a: 'An aluminium mould costs about $1,500 to $10,000 per plastic part and lasts 10,000 to 100,000 shots, enough for a first order. Steel moulds for high volume start near $10,000. Every differently shaped part needs its own mould, so using fewer parts is the quickest way to cut mould cost.',
      },
      {
        q: 'Is this a quote?',
        a: 'No. It\u2019s an educational estimate built from published prices, most of them published by labs and vendors themselves, checked October 15, 2026. Which tests apply depends on your exact product, so confirm with an accredited lab before you budget.',
      },
    ],
  },

  {
    path: '/tam-sam-som-calculator',
    title: 'TAM SAM SOM Calculator: Size Your Market in Minutes',
    description:
      'Calculate TAM, SAM and SOM for a startup or invention, top-down or bottom-up, and see your Year-5 obtainable market with the working shown. Free, no sign-up.',
    breadcrumbLabel: 'TAM SAM SOM Calculator',
    freeTool: {
      footerLabel: 'TAM SAM SOM Calculator',
      category: 'BusinessApplication',
      quickAnswer:
        'TAM is the total yearly spend on what you sell, SAM is the part of it you can actually reach and serve, and SOM is the share of SAM you can realistically win. SOM in Year 5 = TAM today \u00d7 (1 + yearly growth)\u2075 \u00d7 serviceable share \u00d7 obtainable share. For example, a $500M market growing 8% a year, with 20% serviceable and 3% obtainable, gives a Year-5 SOM of about $4.4M.',
    },
    faq: [
      {
        q: 'How do I calculate market size for a startup idea?',
        a: 'Start with the total market (TAM): the yearly spend on the problem you solve, from a published market report or a bottom-up count of buyers \u00d7 price \u00d7 purchases a year. Narrow it to the part you can actually reach and serve (SAM), then to the share you can realistically win in the first few years (SOM). Grow TAM to Year 5 at the market\u2019s yearly growth rate before taking the two shares.',
      },
      {
        q: 'What is the difference between TAM, SAM and SOM?',
        a: 'TAM (total addressable market) is everyone who could buy something like yours. SAM (serviceable available market) is the part you can reach with your product, channel, price and geography. SOM (serviceable obtainable market) is the slice of SAM you can realistically win, which is the number closest to your future revenue.',
      },
      {
        q: 'Should I use top-down or bottom-up market sizing?',
        a: 'Use both if you can. Top-down starts from a published market figure and narrows it, which is quick but easy to inflate. Bottom-up multiplies the number of real potential customers by price and purchases a year, which takes more work but is the version investors trust more, because every input can be checked.',
      },
      {
        q: 'What is a realistic SOM for a new product?',
        a: 'For a new product, a Year-5 SOM of about 1 to 5% of the serviceable market is a common planning range. Above 10% within five years is rare unless the category is new or you have a clear distribution advantage. Treat a high SOM as a claim you will need evidence for.',
      },
      {
        q: 'How do investors read TAM, SAM and SOM?',
        a: 'Investors look at TAM to see whether the opportunity is big enough, and at SOM to see whether your plan is grounded. A huge TAM with an unexplained SOM reads as a guess. A bottom-up SOM that ties to your price, channels and sales capacity reads as a plan.',
      },
      {
        q: 'How do I size the market for a new invention?',
        a: 'Size the problem, not the invention. Find what people spend today on the products or workarounds your invention replaces, count the buyers who have that problem, and multiply by a price they would pay. That gives a bottom-up TAM; the serviceable share comes from the channels and regions you can actually sell in.',
      },
      {
        q: 'Is this calculator\u2019s result accurate?',
        a: 'The arithmetic is exact, but the result is only as good as the numbers you enter. It uses one set of your own estimates, with no research behind them. For sourced figures and pessimistic, realistic and optimistic cases, EntreDash\u2019s Product Strategist researches your actual market and builds a live market model you can download to Excel.',
      },
    ],
  },

  {
    path: '/patent-drawing-cost-calculator',
    title: 'Patent Drawing Cost Calculator: Per-Sheet Prices',
    description:
      'Estimate what patent drawings cost: typical drafter and freelance prices per sheet for utility and design patents, with rush fees. Free, no sign-up.',
    // Market ranges dated September 2026 (drawingCostData.ts RANGES_AS_OF).
    dated: {
      reviewedOn: '2026-10-04',
      title: 'Patent Drawing Cost Calculator: {year} Per-Sheet Prices',
      description: 'Estimate what patent drawings cost in {year}: typical drafter and freelance prices per sheet for utility and design patents, with rush fees. Free, no sign-up.',
    },
    breadcrumbLabel: 'Patent Drawing Cost Calculator',
    freeTool: {
      footerLabel: "Patent Drawing Cost Calculator",
      category: 'FinanceApplication',
      quickAnswer:
        "Formal patent drawings typically cost about $150 to $300 per sheet from an engineer-led drafting firm, so a full seven-view design patent set runs about $1,050 to $2,100. A freelance marketplace set usually costs about $50 to $400, with wide variation in quality, and rush turnaround costs extra. Simple utility inventions often need three to six figures.",
    },
    faq: [
      {
        q: 'How much do patent drawings cost?',
        a: 'Engineer-led patent drafting firms typically charge about $150 to $300 per sheet for formal utility drawings, so a full seven-view design patent set runs about $1,050 to $2,100. A freelance marketplace set usually costs about $50 to $400, with wide variation in quality. Rush turnaround usually costs extra. These are market ranges, not quotes.',
      },
      {
        q: 'How many drawings does a patent need?',
        a: 'Enough to show every feature you claim. Simple utility inventions often need three to six figures; complex devices can need twenty or more. A design patent usually shows the object from seven views: front, back, left, right, top, bottom and a perspective view.',
      },
      {
        q: 'What are the USPTO rules for patent drawings?',
        a: 'The rules are in 37 CFR 1.84: black-and-white line drawings on A4 or US letter sheets with set margins, consistent reference numbers, and no color or photographs without a petition. The USPTO treats drawings as acceptable or unacceptable, and judges them on whether they can be read and reproduced.',
      },
      {
        q: 'Can I use EntreDash drawings in my patent application?',
        a: 'Not as filed drawings. EntreDash makes concept drawings: a numbered sketch, a 3D concept model and a labeled figure sheet. They are a starting point to explain your invention, and many inventors hand them to a patent drafter, which can cut down the back-and-forth. Check every detail yourself before anything is filed.',
      },
      {
        q: 'Is this estimate legal advice?',
        a: 'No. It is an educational estimate from published market prices for drafting services and freelance work, not a quote and not legal advice. Prices vary with complexity, sheet count and turnaround.',
      },
    ],
  },
  {
    path: '/invention-concept-drawings',
    title: 'Invention Concept Drawings from $5.99, Same Day',
    description:
      'A numbered line drawing of your invention, a 3D concept model and a labeled figure sheet. From $5.99, one time. A starting point to pitch, prototype and brief a drafter.',
    breadcrumbLabel: 'Invention Concept Drawings',
    faq: [
      {
        q: 'Can I file these drawings with the USPTO?',
        a: 'No. These are concept drawings, not formal patent drawings. They show your invention clearly with numbered parts, and a patent drafter can prepare formal drawings from them. Check every detail yourself before anything is filed.',
      },
      {
        q: 'Do I own the drawings?',
        a: 'Yes. The drawings, the 3D model and the figure sheet made for your invention are yours to download, share and use.',
      },
      {
        q: 'What kinds of inventions work?',
        a: 'Physical things you can hold or install, and physical devices that work with an app. Software on its own is not drawn as a sketch.',
      },
      {
        q: 'What if the drawing is wrong?',
        a: 'You pick the closest of three options, then redo it up to twice with a note saying what to change. Part numbers can be dragged onto the right part at no cost. If the 3D model needs a change, a rebuild with your note ($14.99) reworks up to 2 parts and keeps the rest.',
      },
      {
        q: 'Does refining my part descriptions with AI cost extra?',
        a: "No. It's included with the 2D Sketch and the Design Set, whether you're refining part descriptions, adding rough sizes or setting out how the parts go together. Before you buy, each idea gets 3 free refines to try it.",
      },
      {
        q: 'How long does it take?',
        a: 'A few minutes. Drafting the parts list takes under a minute, the three sketch options about a minute, and the 3D model a few minutes more.',
      },
      {
        q: 'Can I add the 3D model later?',
        a: 'Yes. Start with the 2D Sketch for $5.99 and add the 3D model and figure sheet any time for $34.00.',
      },
      {
        q: 'Do packs expire?',
        a: 'No. Design Set packs of 3, 5 or 10 never expire and work on any physical invention.',
      },
      {
        q: 'Is my idea kept private?',
        a: 'Yes. Your description and drawings are stored privately and are never used to train AI. A draft you never pay for is deleted after 7 days.',
      },
    ],
  },
  {
    path: '/invention-3d-model',
    title: 'Invention 3D Model: STL, OBJ and GLB from $39.99',
    description:
      'A 3D concept model of your invention to turn, take apart and 3D print, plus a labeled figure sheet and numbered sketch. $39.99 one time, or $34.00 to add to a sketch.',
    breadcrumbLabel: 'Invention 3D Model',
    faq: [
      {
        q: 'Can I 3D print the model?',
        a: 'Yes. Download the STL and open it in your slicer (Cura, Bambu Studio, PrusaSlicer) for a rough print you can hold. Sizes are estimates, so check it in your slicer first.',
      },
      {
        q: 'Which file format do I need?',
        a: 'STL for 3D printing. OBJ or GLB for viewers, Blender and presentations; GLB keeps each part\u2019s colour. SolidWorks, Rhino and AutoCAD open them as a reference shape to trace over, not as a part you can edit.',
      },
      {
        q: 'Is this an engineering file for manufacturing?',
        a: 'No. It is a concept model for seeing, sharing and rough prints. An engineer or product designer can use it as a head start and rebuild the part properly for manufacturing.',
      },
      {
        q: 'What if the model doesn\u2019t match my sketch?',
        a: 'Every model is checked part by part against your sketch, and a rebuild ($14.99) reworks up to 2 parts from a note saying what should change. Earlier versions are kept.',
      },
      {
        q: 'How do you decide how to build my model?',
        a: 'From your sketch and parts list. Boxy, engineered designs are built from measured parts. Round ones use smooth drawn shapes. Soft, organic ones use rounded forms blended together. Clothes and fabric items become a thin layer over the shape they wrap. One-piece sculpted shapes are carved from your sketch\u2019s outlines. Your model page says which one we used and why.',
      },
      {
        q: 'Can I change the model myself?',
        a: 'Yes. Move, resize and nudge any part, accept the fixes our checks suggest, and on clothing change the fabric\u2019s thickness. Edits are free and every version is kept, so you can always go back. For boxy designs, you can also choose \u201cGuide me\u201d and check each part as it\u2019s made.',
      },
      {
        q: 'Does it work for clothing and fabric products?',
        a: 'Yes. Jackets, gloves, bags, pillows and tents are built as a thin fabric layer over the shape they wrap, with openings, seams and pockets as their own numbered parts. You get a 3D look-and-fit model, not a flat sewing pattern.',
      },
      {
        q: 'Can I get more realistic renders?',
        a: 'Yes. Each model comes with a render and a redo. Pick the angle, the finish (matte black, brushed metal, clear plastic and more) and the setting (studio, in a hand, on a desk, outdoors), and add your own direction for colour, light or mood. The shape always comes from your model. More renders come in packs of 5 ($8.99), 15 ($25.99) or 40 ($67.99). They never expire, work on any model, and a render that fails uses nothing.',
      },
      {
        q: 'What is the figure sheet?',
        a: 'A black-and-white PDF of your numbered drawings, one figure per page: the sketch and each part close-up, plus a parts list. It comes with the 2D Sketch. The Design Set adds the model\u2019s assembled, front, top, side and exploded views, so the sheet shows every side and how the parts come apart. If the model doesn\u2019t match your sketch yet, we tell you which parts differ and how to fix them first.',
      },
    ],
  },
  {
    path: '/invention-concept-drawings/sample',
    title: 'Invention Drawing Example: Sketch, 3D Model, Sheet',
    description:
      'A complete worked example: the description, drafted parts list, three sketch options, the numbered sketch, a 3D concept model and the figure sheet, with real files.',
    breadcrumbLabel: 'Invention Drawing Example',
  },

  // -------------------------------------------------------------------------
  // Sample deliverables (/demo/*). Real, worked examples of what EntreDash
  // produces, linked from the homepage. Registered here so each is a distinct,
  // indexable landing page with its own canonical, title, description, and
  // breadcrumb — previously they rendered the bare SPA shell to crawlers (no
  // canonical, generic title), so Google and audit tools saw them as thin
  // duplicates. Add each path to web.json's prerender.paths too (the manual
  // mirror noted above).
  // -------------------------------------------------------------------------
  {
    path: '/demo/memo',
    title: 'Sample Invention Strategy Memo — EntreDash',
    description:
      'A real EntreDash invention strategy memo: a patentability read on novelty, non-obviousness, and usefulness, with drafted claims and a filing path.',
    breadcrumbLabel: 'Sample Invention Strategy Memo',
  },
  {
    path: '/demo/sample-needs-discovery',
    title: 'Sample Market Validation Memo — EntreDash',
    description:
      'A real EntreDash needs-discovery memo: tests whether the market need is real, sizes it with TAM, SAM, and SOM, and ends in a verdict backed by live data.',
    breadcrumbLabel: 'Sample Market Validation Memo',
  },
  {
    path: '/demo/sample-customer-discovery',
    title: 'Sample Customer Discovery Kit — EntreDash',
    description:
      'A real EntreDash customer discovery kit: Mom Test-style interview questions and a field guide for finding out whether customers will pay.',
    breadcrumbLabel: 'Sample Customer Discovery Kit',
  },
  {
    path: '/demo/sample-search-strategy',
    title: 'Sample Prior-Art Search Strategy — EntreDash',
    description:
      'A real EntreDash prior-art search plan: the queries, keywords, and patent classifications used to find what already exists before you file.',
    breadcrumbLabel: 'Sample Prior-Art Search Strategy',
  },
  {
    path: '/demo/sample-search-report',
    title: 'Sample Prior-Art Search Report — EntreDash',
    description:
      'A real EntreDash prior-art search report: the patents and references found, how close each one comes to the idea, and what that means for filing.',
    breadcrumbLabel: 'Sample Prior-Art Search Report',
  },
  {
    path: '/demo/sample-design',
    title: 'Sample Design Breakdown — EntreDash',
    description:
      'A real EntreDash design breakdown: an exploded view of the parts, the materials each uses, and what it takes to build the invention.',
    breadcrumbLabel: 'Sample Design Breakdown',
  },
  {
    path: '/demo/sample-business-case',
    title: 'Sample 5-Year Business Case — EntreDash',
    description:
      'A real EntreDash business case: a five-year financial model with revenue, EBITDA, and breakeven, built to test whether the numbers hold up.',
    breadcrumbLabel: 'Sample Business Case',
  },
  {
    path: '/demo/sample-business-model-canvas',
    title: 'Sample Business Model Canvas — EntreDash',
    description:
      'A real EntreDash business model canvas: all nine building blocks on one page, showing how the business fits together before you scale.',
    breadcrumbLabel: 'Sample Business Model Canvas',
  },
  {
    path: '/demo/sample-spark',
    title: 'Sample Spark: Business Directions — EntreDash',
    description:
      'A worked example of an EntreDash Spark: an everyday frustration turned into concrete, buildable business directions, each with a quick read on its market.',
    breadcrumbLabel: 'Sample Spark',
  },
];

/** The registry with each time-sensitive page's year applied (see `dated`). */
export const PUBLIC_PAGES: PublicPage[] = REGISTERED_PAGES.map((p) => withCurrentYear(p));

/** Every registered public-page path, in registration order. */
export const PUBLIC_PAGE_PATHS: string[] = PUBLIC_PAGES.map((p) => p.path);

/**
 * The free, no-signup tool pages surfaced in the site footer's "Free Tools"
 * column, in registration order. Derived from the `freeTool` tag so the footer
 * never drifts from the registry — register a calculator with a `freeTool` label
 * and it appears in the footer automatically.
 */
export const FREE_TOOL_PAGES: PublicPage[] = PUBLIC_PAGES.filter((p) => p.freeTool);

/** Registry keyed by exact pathname, for O(1) per-route lookups. */
export const PUBLIC_PAGES_BY_PATH: Record<string, PublicPage> = Object.fromEntries(
  PUBLIC_PAGES.map((p) => [p.path, p]),
);

/**
 * The static (non-blog) paths that belong in sitemap.xml: every registered page
 * except any explicitly opted out (`includeInSitemap: false`). The blog index
 * and per-post URLs are added by the sitemap builder itself, from the live
 * published-post read — they are not static registry entries.
 */
export function sitemapStaticPaths(): string[] {
  return PUBLIC_PAGES.filter((p) => p.includeInSitemap !== false).map((p) => p.path);
}
